import React, {useEffect, useReducer, useState} from "react";
import Table from "./components/FilingTable";
import {filingReducer, filingReducerInit, INITIAL_STATE} from "./utils/filingReducer";
import Container from "@amzn/awsui-components-react/polaris/container";
import {useLocation} from "react-router-dom";
import { statusColorCodes, TRANSACTION_DETAILS_TABLE_COLUMNS } from "../../common/utils/commonUtils";
import EllipsisText from "../../common/ellipsisText/EllipsisText";
import HoverTooltip from "../../common/ellipsisText/HoverTooltip";
import {formatDateWithTime} from "../../common/utils/apiUtils";
import { Alert, Box, Button, ColumnLayout, Flashbar, Spinner } from "@amzn/awsui-components-react";
import {COLOR_RED, TRANSACTION_DETAIL_INFO} from "../../common/constants/constants";
import {
    fetchCountry,
    getAuditClientId,
    getFileNameFromS3Key,
    getGeneratedReportName,
    getTransformationClientId,
    getTransformationId,
    getTransformationSubUseCase,
    hasSubmissionReceipt,
    hasTransactionInputFile,
    isTransactionInProgress,
    noFileHint,
    resolveTransactionErrorDetails
} from "./utils/Utils";
import {downloadFolder} from "../../common/utils/s3Utils";
import {getFilingReceipt} from "./utils/filingAPIs";
import {getGeneratedFileLabel, isTransformationRoute} from "./components/filingTableConfig";
import {
    downloadDocumentBys3PreSignedURL,
    downloadGeneratedFileByTransactionId,
    fetchAuditRecordByTransactionId
} from "../excalibur/components/excaliburAPIs";
import {ERROR_REPORT_DOWNLOAD_FAILURE_FLASH, EXCALIBUR_STATUS} from "../excalibur/constants/constants";
import {DOWNLOAD_INITIAL_STATE, downloadReducer, downloadReducerInit} from "../exception/components/common/downloadDocumentReducer";
import AppConfig from "../../AppConfig";

function TransactionDetails(params) {
    const [filingData, filingDispatcher] = useReducer(filingReducer,
        INITIAL_STATE, filingReducerInit);
    const [downloadDocument, downloadDocumentDispatcher] = useReducer(downloadReducer,
        DOWNLOAD_INITIAL_STATE, downloadReducerInit);
    const [loading, setLoading] = useState(false);
    const [filedReportName, setFiledReportName] = useState(null);
    const [auditRecord, setAuditRecord] = useState(null);

    const location = useLocation();
    const passStateData = location?.state?.data;
    // The list's copy of the row, until the record's own fetch answers; a reload otherwise keeps showing it.
    const auditRow = auditRecord ?? passStateData;
    const flashBarItems = filingData?.flashBarItem ?? [];

    useEffect(() => {
        const clientId = getAuditClientId(passStateData);
        if (!passStateData?.transactionId || !clientId) {
            return undefined;
        }
        let cancelled = false;
        fetchAuditRecordByTransactionId({transactionId: passStateData.transactionId, clientId})
            .then(record => {
                if (!cancelled && record) {
                    setAuditRecord(record);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [passStateData]);

    const isTransformation = isTransformationRoute(location?.pathname);
    // What a filing sent is its transformation's generated file, signed by that transaction's id.
    const transformationId = isTransformation ? null : getTransformationId(auditRow);
    const transformationClientId = getTransformationClientId(auditRow);

    // Names the filing header's Generated Report from the transformation's own record; stays "Download" otherwise.
    useEffect(() => {
        if (!transformationId || !transformationClientId) {
            return undefined;
        }
        let cancelled = false;
        fetchAuditRecordByTransactionId({transactionId: transformationId, clientId: transformationClientId})
            .then(record => {
                if (!cancelled && record) {
                    setFiledReportName(getGeneratedReportName(record));
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [transformationId, transformationClientId]);

    if (!auditRow?.transactionId) {
        return (
            <div className="transactionDetails">
                <Container>
                    <p style={{ color: COLOR_RED, fontWeight: "bold" }}>
                        Transaction ID is missing. Unable to display details.
                    </p>
                </Container>
            </div>
        );
    }

    const reportDownloadFailure = () => {
        filingDispatcher({
            type: "SET_FLASH_BAR", payload: {
                msg: 'Error occurred while downloading the file',
                type: 'error',
                dispatcher: filingDispatcher
            }
        });
    };

    const reportNoFileFound = () => {
        filingDispatcher({
            type: "SET_FLASH_BAR", payload: {
                msg: 'No file is available to download yet for this transaction',
                type: 'info',
                dispatcher: filingDispatcher
            }
        });
    };

    const downloadTransactionFolder = async (s3Key, s3Bucket) => {
        setLoading(true);
        try {
            const downloaded = await downloadFolder(s3Key, s3Bucket, params.domain);
            if (downloaded === 0) {
                reportNoFileFound();
            }
        } catch (error) {
            reportDownloadFailure();
        } finally {
            setLoading(false);
        }
    };

    const downloadTransactionInputFile = () =>
        downloadTransactionFolder(auditRow.inputFileKey, auditRow.inputFileBucket);

    // Same as the main filing page: read the receipt from S3 if it was written, otherwise fetch it
    // from the authority.
    const downloadTransactionReceipt = () => {
        if (auditRow.generatedFileKey) {
            downloadTransactionFolder(auditRow.generatedFileKey,
                AppConfig[params.domain].FILING_RECEIPT_BUCKET);
        } else {
            getFilingReceipt(filingDispatcher, auditRow.useCase, auditRow.subUseCase,
                auditRow.transactionId, params.domain, setLoading);
        }
    };

    // Same as the Excalibur table's Error Details column: a failed transformation's error report.
    const downloadErrorReport = async () => {
        setLoading(true);
        try {
            await downloadGeneratedFileByTransactionId({
                useCase: auditRow.useCase,
                subUseCase: auditRow.subUseCase,
                transactionId: auditRow.transactionId
            });
        } catch (error) {
            filingDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: ERROR_REPORT_DOWNLOAD_FAILURE_FLASH.message,
                    type: 'error',
                    dispatcher: filingDispatcher
                }
            });
        } finally {
            setLoading(false);
        }
    };

    // Same as the Excalibur table's Output File column: the generated report is a DMS document.
    // Failures are reported through downloadDocumentDispatcher's own flash bar.
    const downloadGeneratedReport = async () => {
        setLoading(true);
        try {
            await downloadDocumentBys3PreSignedURL(auditRow.documentId, auditRow.useCase,
                auditRow.subUseCase, downloadDocumentDispatcher);
        } finally {
            setLoading(false);
        }
    };

    const hasInputFile = hasTransactionInputFile(auditRow);
    const hasGeneratedFile = isTransformation
        ? Boolean(auditRow.documentId)
        : hasSubmissionReceipt(auditRow);
    const downloadGeneratedFile = isTransformation ? downloadGeneratedReport : downloadTransactionReceipt;
    const generatedFileLabel = getGeneratedFileLabel(isTransformation);

    const downloadFiledReport = async () => {
        setLoading(true);
        try {
            // The transformation's scope, as the name lookup and the Excalibur page use for this file.
            await downloadGeneratedFileByTransactionId({
                useCase: auditRow.useCase,
                subUseCase: getTransformationSubUseCase(auditRow),
                transactionId: transformationId
            });
        } catch (error) {
            reportDownloadFailure();
        } finally {
            setLoading(false);
        }
    };

    // A transformation's report name follows the Excalibur table; a filing's receipt is the stored
    // file, if any. A key that is a folder, or a receipt fetched on demand, has no name to show.
    const inputFileName = getFileNameFromS3Key(auditRow.inputFileKey);
    const generatedFileName = isTransformation
        ? getGeneratedReportName(auditRow)
        : getFileNameFromS3Key(auditRow.generatedFileKey);

    const errorDetailsText = resolveTransactionErrorDetails(auditRow);
    const hasErrorReport = isTransformation
        && auditRow.status === EXCALIBUR_STATUS.FAILURE
        && Boolean(auditRow.generatedFileKey);
    const requestCreatedOn = (
        <div><b>Request Created On:</b> {formatDateWithTime(auditRow?.startTimestamp)}</div>
    );
    const requestedBy = <div><b>Requested By:</b> {auditRow?.createdBy}</div>;

    return (
        <>
            <div className="transactionDetails">
                {loading ? <Spinner className={"appLoader"} size="large" variant={"disabled"}>Loading</Spinner> : null}
                {/*
                  * The table's error paths report by dispatching SET_FLASH_BAR, so the page has to
                  * render what that produces or a failed download tells the user nothing. The
                  * reducer only wires onDismiss when it is also given a call-to-action handler,
                  * which these messages have none of, so dismissal is wired here instead.
                  */}
                {flashBarItems.length > 0 &&
                    <Flashbar items={flashBarItems.map(item => ({
                        ...item,
                        onDismiss: () => filingDispatcher({ type: "DISMISS_FLASHBAR", payload: {} })
                    }))} />
                }
                {downloadDocument?.flashBarItem?.length > 0 &&
                    <Flashbar items={downloadDocument.flashBarItem} />
                }
                {isTransactionInProgress(auditRow) &&
                    <Alert type="info" statusIconAriaLabel="Info">
                        This file is being processed. You will receive an email once it is complete.
                    </Alert>
                }
                <Container
                    header={
                        <h2>Transaction Details</h2>
                    }>
                    <Container>
                        <ColumnLayout columns={3}>
                            <div><b>Transaction ID:</b> {auditRow?.transactionId}</div>
                            <div><b>Country:</b> {fetchCountry(auditRow?.useCase) === "Compliance"
                                ? auditRow?.useCase
                                : fetchCountry(auditRow?.useCase)}</div>
                            <div><b>Filing Type:</b> {auditRow?.subUseCase}</div>
                        </ColumnLayout>
                        {/* Only Excalibur produces an error report; only that route shows the cell. */}
                        <ColumnLayout columns={3}>
                            {!isTransformation && requestCreatedOn}
                            <div><b>Status:</b> <span
                                style={statusColorCodes(auditRow?.status)}>{auditRow?.status}</span></div>
                            <div className="ellipsisTextRow">
                                <b>Error Details:</b>
                                <EllipsisText text={errorDetailsText} fallback="N/A" fill style={{ color: COLOR_RED }} />
                            </div>
                            {isTransformation &&
                                <div>
                                    <b>Error Report:</b>{" "}
                                    <HoverTooltip text={hasErrorReport ? null : noFileHint("Error Report")}>
                                        <Button iconName="download" variant={"inline-link"}
                                                disabled={!hasErrorReport || loading}
                                                onClick={downloadErrorReport}>Download</Button>
                                    </HoverTooltip>
                                </div>
                            }
                        </ColumnLayout>
                        <ColumnLayout columns={3}>
                            <div>
                                <b>Input File:</b>{" "}
                                <HoverTooltip text={hasInputFile ? null : noFileHint("Input File")}>
                                    <Button iconName="download" variant={"inline-link"}
                                            disabled={!hasInputFile || loading}
                                            onClick={downloadTransactionInputFile}>
                                        <EllipsisText text={inputFileName} fallback="Download" />
                                    </Button>
                                </HoverTooltip>
                            </div>
                            {!isTransformation &&
                                <div>
                                    <b>Generated Report:</b>{" "}
                                    <HoverTooltip text={transformationId ? null : noFileHint("Generated Report")}>
                                        <Button iconName="download" variant={"inline-link"}
                                                disabled={!transformationId || loading}
                                                onClick={downloadFiledReport}>
                                            <EllipsisText text={filedReportName} fallback="Download" />
                                        </Button>
                                    </HoverTooltip>
                                </div>
                            }
                            <div>
                                <b>{generatedFileLabel}:</b>{" "}
                                <HoverTooltip text={hasGeneratedFile ? null : noFileHint(generatedFileLabel)}>
                                    <Button iconName="download" variant={"inline-link"}
                                            disabled={!hasGeneratedFile || loading}
                                            onClick={downloadGeneratedFile}>
                                        <EllipsisText text={generatedFileName} fallback="Download" />
                                    </Button>
                                </HoverTooltip>
                            </div>
                            {isTransformation && requestedBy}
                        </ColumnLayout>
                        <Box padding={{ top: "s" }}>{isTransformation ? requestCreatedOn : requestedBy}</Box>
                    </Container>
                </Container>
                <div>
                <div className="table">
                    <Table
                            {...params}
                            table={filingData}
                            tableDispatcher={filingDispatcher}
                            tableHeader="Transaction Submission Summary"
                            visibleColumns={TRANSACTION_DETAILS_TABLE_COLUMNS}
                            transactionId={auditRow?.transactionId}
                            clientId={getAuditClientId(auditRow)}
                            tableContext={TRANSACTION_DETAIL_INFO}
                            parentAuditRow={auditRow}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

export default TransactionDetails;
